import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { crisisData } from '../data/crisisData';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Simple, clean crisis marker for dashboard
const createCrisisIcon = (severity, type) => {
  const colors = {
    5: '#DC2626', // Critical - Red
    4: '#F97316', // High - Orange  
    3: '#EAB308', // Medium - Yellow
    2: '#3B82F6', // Low - Blue
    1: '#059669', // Minimal - Green
  };
  
  const size = severity >= 4 ? 32 : severity >= 3 ? 28 : 24;
  const borderWidth = severity >= 4 ? 3 : 2;

  return L.divIcon({
    html: `
      <div style="
        background-color: ${colors[severity] || '#6B7280'};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: ${borderWidth}px solid white;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
        font-weight: bold;
        color: white;
        font-size: 11px;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        cursor: pointer;
      ">
        ${severity}
      </div>
    `,
    className: 'crisis-marker-clean',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2 - 5]
  });
};

const DashboardMap = ({ height = '500px', showControls = true, onCrisisSelect, crisis, singleCrisis = false, defaultSatellite = false }) => {
  // Debug environment variables and test Mapbox connectivity
  React.useEffect(() => {
    console.log('=== MAPBOX DEBUG INFO ===');
    console.log('REACT_APP_MAPBOX_TOKEN:', process.env.REACT_APP_MAPBOX_TOKEN ? 'Present' : 'Missing');
    console.log('Token starts with:', process.env.REACT_APP_MAPBOX_TOKEN ? process.env.REACT_APP_MAPBOX_TOKEN.substring(0, 15) + '...' : 'N/A');
    
    // Test the actual tile URL
    const testTileUrl = `https://api.mapbox.com/styles/v1/samfrons/cme0gwcul01f101s22ya6ag2i/tiles/256/1/1/1@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`;
    console.log('Testing tile URL:', testTileUrl);
    
    // Test if we can fetch a sample tile
    if (process.env.REACT_APP_MAPBOX_TOKEN) {
      fetch(testTileUrl, { method: 'HEAD' })
        .then(response => {
          console.log('Mapbox tile test status:', response.status);
          if (!response.ok) {
            console.error('Mapbox tile failed:', response.status, response.statusText);
          } else {
            console.log('✓ Mapbox tiles are accessible');
          }
        })
        .catch(error => {
          console.error('Mapbox tile test failed:', error);
        });
    }
    console.log('========================');
  }, []);

  // Use either single crisis or all crises data
  const [crises] = useState(
    singleCrisis && crisis ? [crisis] : crisisData.slice(0, 10)
  );

  const [showSatellite, setShowSatellite] = useState(defaultSatellite);
  const [useMapbox, setUseMapbox] = useState(!!process.env.REACT_APP_MAPBOX_TOKEN);
  
  // Set map center and zoom based on crisis location or default
  const [mapCenter] = useState(
    singleCrisis && crisis?.latitude && crisis?.longitude 
      ? [crisis.latitude, crisis.longitude]
      : [25, 35]  // Centered on Middle East/North Africa region
  );
  const [mapZoom] = useState(singleCrisis ? 8 : 4);  // Increased default zoom for better regional view

  // Handle tile load errors - fallback to OpenStreetMap
  const handleTileError = (error) => {
    console.warn('Mapbox tiles failed to load, falling back to OpenStreetMap:', error);
    setUseMapbox(false);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
  };

  return (
    <div className="relative w-full" style={{ height }}>
      {/* Simple Controls - Only show if requested */}
      {showControls && (
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden" style={{ zIndex: 9999 }}>
          <button 
            className={`px-3 py-2 text-sm font-medium transition-colors border-b border-gray-200 ${
              !showSatellite 
                ? 'bg-accent-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setShowSatellite(false)}
          >
            Map
          </button>
          <button 
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              showSatellite 
                ? 'bg-accent-500 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setShowSatellite(true)}
          >
            Satellite
          </button>
        </div>
      )}

      {/* Clean Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3" style={{ zIndex: 9999 }}>
        <h4 className="text-sm font-semibold text-gray-800 mb-2">Crisis Severity</h4>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Critical (5)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">High (4)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Medium (3)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full border border-white"></div>
            <span className="text-xs text-gray-600">Low (2-1)</span>
          </div>
        </div>
      </div>

      <MapContainer 
        center={mapCenter} 
        zoom={mapZoom} 
        style={{ height: '100%', width: '100%' }}
        className="dashboard-map"
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url={
            showSatellite 
              ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
          attribution={showSatellite 
            ? '© Esri, Maxar, Earthstar Geographics, and the GIS User Community'
            : '© OpenStreetMap contributors'
          }
        />
        
        {/* Crisis Markers */}
        {crises.map(crisis => (
          crisis.latitude && crisis.longitude && (
            <Marker
              key={crisis.id}
              position={[crisis.latitude, crisis.longitude]}
              icon={createCrisisIcon(crisis.severity, crisis.type)}
            >
              <Popup className="crisis-popup-clean">
                <div className="p-2 min-w-64">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2">{crisis.title}</h3>
                  <div className="space-y-1 text-xs text-gray-600">
                    <div className="flex justify-between">
                      <span>Location:</span>
                      <span className="font-medium text-gray-800">{crisis.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Severity:</span>
                      <span className="font-medium text-gray-800">{crisis.severity}/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Affected:</span>
                      <span className="font-medium text-gray-800">{formatNumber(crisis.affected_population)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`font-medium px-1.5 py-0.5 rounded text-xs ${
                        crisis.status === 'active' 
                          ? 'bg-red-100 text-red-700'
                          : crisis.status === 'monitoring'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {crisis.status}
                      </span>
                    </div>
                  </div>
                  {!singleCrisis && (
                    <div className="mt-3 pt-2 border-t border-gray-200">
                      <button 
                        className="w-full bg-accent-500 text-white text-xs py-1.5 px-3 rounded hover:bg-accent-600 transition-colors font-medium"
                        onClick={() => onCrisisSelect && onCrisisSelect(crisis)}
                      >
                        View Details
                      </button>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          )
        ))}
      </MapContainer>
    </div>
  );
};

export default DashboardMap;